import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Eye, GraduationCap, Heart, Loader2, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import logo from "@/assets/logo.png";
import { resolveImage } from "@/lib/api";
import { bestDiscount, errMsg, euros, ProductPreviewModal } from "@/pages/EcoleParent";
import RelativeCheckout from "@/components/RelativeCheckout";

const relApi = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`, withCredentials: true });

export default function RelativeOrderPage() {
  const { token } = useParams();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [qty, setQty] = useState({});
  const [preview, setPreview] = useState(null);
  const [done, setDone] = useState(null);

  useEffect(() => {
    relApi.get(`/partage/${token}`).then(({ data: d }) => setData(d)).catch(() => setData(false));
  }, [token]);

  useEffect(() => {
    const orderId = params.get("commande");
    if (!orderId) return;
    relApi.get(`/partage/${token}/commande/${orderId}`, { params: { session_id: params.get("session_id") || params.get("token") || "" } })
      .then(({ data: o }) => setDone({ ...o, order_id: orderId })).catch(() => toast.error("Commande introuvable"));
    if (params.get("annule")) toast.error("Paiement annulé");
  }, [params, token]);

  const products = data?.products || [];
  const totalQty = products.reduce((s, p) => s + (qty[p.product_id] || 0), 0);
  const cart = useMemo(() => products.filter((p) => qty[p.product_id] > 0).map((p) => {
    const q = qty[p.product_id];
    const disc = bestDiscount(p, q, data.price_tiers, totalQty);
    return { ...p, q, disc, unit: disc ? Math.round(p.price_cents * (100 - disc) / 100) : p.price_cents };
  }), [qty, products, data, totalQty]);
  const subtotal = cart.reduce((s, p) => s + p.unit * p.q, 0);

  if (data === null) return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="animate-spin text-amber-deep" size={28} /></div>;
  if (data === false) return <p data-testid="relative-invalid" className="py-32 text-center font-serif text-2xl text-bark-soft">Ce lien de commande n'est plus valide.</p>;

  const { school, child } = data;
  const forWhom = child ? `${child.first} (${child.class})` : "un élève";

  return (
    <div data-testid="relative-order-page" className="mx-auto max-w-5xl px-5 pb-24 pt-10 sm:px-8">
      <div className="flex items-center gap-4">
        <img src={logo} alt="Pétales de Miel" className="h-14 w-14" />
        <div>
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-amber-deep"><GraduationCap size={15} /> {school.name}{school.city ? ` · ${school.city}` : ""}</p>
          <h1 className="font-serif text-3xl font-semibold text-bark sm:text-4xl">Une commande qui fait plaisir à {child ? child.first : "l'école"}</h1>
        </div>
      </div>
      <p data-testid="relative-for" className="mt-4 flex items-start gap-2 rounded-2xl bg-amber-50 px-5 py-4 text-sm text-bark">
        <Heart size={17} className="mt-0.5 shrink-0 fill-terra text-terra" />
        <span>Votre commande est comptée pour <strong>{forWhom}</strong> et aide l'école {school.name}. Les produits sont remis à l'enfant à l'école{school.deadline ? ` — commandes ouvertes jusqu'au ${new Date(school.deadline).toLocaleDateString("fr-FR")}` : ""}.</span>
      </p>

      {done ? (
        <div data-testid="relative-done" className="mt-8 rounded-3xl border border-sage/30 bg-white p-8 text-center shadow-sm">
          <CheckCircle2 size={40} className="mx-auto text-sage" />
          <p className="mt-3 font-serif text-3xl font-semibold text-bark">Merci pour votre commande !</p>
          <p className="mt-2 text-bark-soft">
            Commande n° {done.order_id.slice(-8).toUpperCase()} · {euros(done.subtotal_cents)} €.{" "}
            {done.status === "paid" ? "Paiement par carte confirmé." : done.payment_method === "card" ? "Paiement en cours de confirmation." : `Remettez votre chèque à ${child ? child.first : "l'élève"} pour l'école.`}
          </p>
          <button data-testid="relative-new-order" onClick={() => { setDone(null); setQty({}); setParams({}); }} className="mt-5 rounded-full border border-amber-800/25 px-5 py-2.5 text-sm font-medium text-bark hover:border-amber-deep">Passer une autre commande</button>
        </div>
      ) : school.closed ? (
        <p data-testid="relative-closed" className="mt-8 rounded-2xl bg-terra/10 px-5 py-6 text-center text-terra">La campagne de commande de cette école est clôturée.</p>
      ) : (
        <div className="mt-8 grid gap-8 lg:grid-cols-[1.5fr_1fr]">
          <ul className="space-y-3">
            {products.map((p) => {
              const q = qty[p.product_id] || 0;
              return (
                <li key={p.product_id} data-testid={`relative-product-${p.product_id}`} className="flex gap-4 rounded-2xl border border-amber-800/15 bg-white p-4 shadow-sm">
                  <img src={resolveImage(p.image_url)} alt="" className="h-16 w-16 shrink-0 rounded-xl bg-white object-contain" />
                  <div className="min-w-0 flex-1">
                    <button type="button" data-testid={`relative-preview-${p.product_id}`} onClick={() => setPreview(p)} className="text-left font-medium text-bark hover:text-amber-deep">{p.name}{p.weight && !p.name.toLowerCase().includes(p.weight.toLowerCase()) ? ` - ${p.weight}` : ""} <Eye size={13} className="inline text-stone-mute" /></button>
                    <p className="text-sm font-semibold text-amber-deep">{euros(p.price_cents)} €</p>
                    <div className="mt-2 flex items-center gap-3">
                      <button data-testid={`relative-minus-${p.product_id}`} onClick={() => setQty((x) => ({ ...x, [p.product_id]: Math.max(0, q - 1) }))} aria-label="Retirer" className="flex h-8 w-8 items-center justify-center rounded-full border border-amber-800/25 text-bark"><Minus size={14} /></button>
                      <span data-testid={`relative-qty-${p.product_id}`} className="w-6 text-center font-semibold">{q}</span>
                      <button data-testid={`relative-plus-${p.product_id}`} onClick={() => setQty((x) => ({ ...x, [p.product_id]: q + 1 }))} aria-label="Ajouter" className="flex h-8 w-8 items-center justify-center rounded-full border border-amber-deep text-amber-deep"><Plus size={14} /></button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
          <RelativeCheckout api={relApi} token={token} cart={cart} subtotal={subtotal} childName={child?.first} pointsPerEuro={data.points_per_euro} onDone={setDone} errMsg={errMsg} />
        </div>
      )}
      {cart.length > 0 && !done && (
        <div data-testid="relative-mobile-bar" className="fixed inset-x-0 bottom-0 z-40 flex items-center justify-between gap-3 border-t border-amber-800/15 bg-white/95 px-5 py-3 backdrop-blur lg:hidden">
          <span className="text-sm text-bark"><strong>{totalQty}</strong> article{totalQty > 1 ? "s" : ""} · <strong>{euros(subtotal)} €</strong></span>
          <button data-testid="relative-mobile-go" onClick={() => document.querySelector("[data-testid=relative-checkout]")?.scrollIntoView({ behavior: "smooth" })} className="rounded-full bg-amber-deep px-5 py-2.5 text-sm font-semibold text-white">Commander</button>
        </div>
      )}
      {preview && <ProductPreviewModal product={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}
